import { useState, useRef, useCallback, useEffect } from "react";
import { Send, Loader2, Paperclip, X } from "lucide-react";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { format } from "date-fns";
import { uploadApi } from "@/lib/api-client";
import { useAuthStore } from "@/stores/auth.store";
import { cn, getUploadUrl, MAX_ATTACHMENT_SIZE, formatBytes, extractErrorMessage } from "@/lib/utils";
import { trpc, trpcClient } from "@/lib/trpc";
import { toast } from "sonner";
import ImageLightbox from "@/components/shared/ImageLightbox";
import { MarkdownRenderer } from "@/components/shared/MarkdownRenderer";

import { useConfirm } from "@/providers/ConfirmProvider";

interface CommentSectionProps {
    entityId: string;
    entityType: string;
}

function renderContent(content: string) {
    if (!content) return null;

    // Lấy API_URL để tự động thêm vào link /uploads/...
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3006/api';
    const backendUrl = apiUrl.replace('/api', '');

    const resolveUrl = (url: string) => {
        if (url.startsWith('/uploads/')) return backendUrl + url;
        return url;
    };

    const getPreviewUrl = (fileName: string, fileUrl: string) => {
        const ext = fileName.split('.').pop()?.toLowerCase() || '';
        if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(ext)) {
            return `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(fileUrl)}`;
        }
        return fileUrl;
    };

    const parts = content.split(/(!?\[.*?\]\(.*?\))/g);
    return parts.map((part, i) => {
        const imageMatch = part.match(/^!\[(.*?)\]\((.*?)\)$/);
        if (imageMatch) {
            const [, alt, url] = imageMatch;
            const resolved = resolveUrl(url);
            return (
                <ImageLightbox key={i} src={resolved} alt={alt}>
                    <img src={resolved} alt={alt} className="block max-w-full rounded-md mt-2 mb-2 max-h-48 object-contain border border-slate-200 shadow-sm cursor-pointer hover:opacity-90 transition-opacity" />
                </ImageLightbox>
            );
        }
        const fileMatch = part.match(/^\[(.*?)\]\((.*?)\)$/);
        if (fileMatch) {
            const [, text, url] = fileMatch;
            const resolved = resolveUrl(url);
            const ext = text.split('.').pop()?.toLowerCase() || '';
            const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
            if (isImage) {
                return (
                    <ImageLightbox key={i} src={resolved} alt={text}>
                        <span className="inline-flex items-center gap-1.5 text-indigo-600 hover:text-indigo-700 hover:underline mt-1 bg-indigo-50/50 px-2 py-1 rounded border border-indigo-100 text-xs w-max cursor-pointer">
                            <Paperclip className="h-3.5 w-3.5" />
                            {text}
                        </span>
                    </ImageLightbox>
                );
            }
            
            const previewHref = getPreviewUrl(text, resolved);
            return (
                <a key={i} href={previewHref} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-indigo-600 hover:text-indigo-700 hover:underline mt-1 bg-indigo-50/50 px-2 py-1 rounded border border-indigo-100 text-xs w-max">
                    <Paperclip className="h-3.5 w-3.5" />
                    {text}
                </a>
            );
        }
        return <MarkdownRenderer key={i} content={part} />;
    });
}

export default function CommentSection({ entityId, entityType }: CommentSectionProps) {
    const queryClient = useQueryClient();
    const [newComment, setNewComment] = useState("");
    const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
    const [editContent, setEditContent] = useState("");
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const scrollRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [pendingAttachments, setPendingAttachments] = useState<{fileName: string, fileUrl: string, isImage: boolean}[]>([]);
    const isComposingRef = useRef(false);
    const authUser = useAuthStore((state) => state.user);
    const currentUser = {
        full_name: authUser?.fullName || "Người dùng",
        user_id: authUser?.id || "unknown",
        avatar: authUser?.avatar || null,
    };
    const confirm = useConfirm();

    // Fetch comments
    const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery(
        trpc.comments.getByEntity.infiniteQueryOptions(
            { entityType, entityId },
            { 
                getNextPageParam: (lastPage: any) => lastPage.nextCursor,
                enabled: !!entityId && entityId !== "Đang tải..."
            }
        )
    );

    const comments = data?.pages.flatMap(page => page?.items || []).filter(Boolean) || [];

    const scrollToTop = useCallback(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = 0;
        }
    }, []);

    // Create mutation with blazing-fast Optimistic UI (<10ms perceived latency)
    const createMutation = useMutation(
        trpc.comments.create.mutationOptions({
            onMutate: async (newCommentInput) => {
                const queryFilter = { queryKey: trpc.comments.getByEntity.queryKey({ entityType, entityId }) };
                await queryClient.cancelQueries(queryFilter);
                const previousData = queryClient.getQueriesData(queryFilter);

                const optimisticComment = {
                    id: `temp-${Date.now()}`,
                    content: newCommentInput.content,
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                    userId: currentUser.user_id,
                    authorId: currentUser.user_id,
                    author: {
                        id: currentUser.user_id,
                        fullName: currentUser.full_name,
                        avatar: currentUser.avatar,
                    },
                    userName: currentUser.full_name,
                    isOptimistic: true,
                };

                queryClient.setQueriesData(queryFilter, (old: any) => {
                    if (!old || !old.pages || old.pages.length === 0) {
                        return {
                            pages: [{ items: [optimisticComment], nextCursor: null }],
                            pageParams: [undefined],
                        };
                    }
                    const firstPage = old.pages[0];
                    return {
                        ...old,
                        pages: [
                            {
                                ...firstPage,
                                items: [optimisticComment, ...(firstPage.items || [])],
                            },
                            ...old.pages.slice(1),
                        ],
                    };
                });

                setNewComment("");
                if (inputRef.current) {
                    inputRef.current.style.height = '60px';
                }
                setTimeout(scrollToTop, 20);

                return { previousData, queryFilter };
            },
            onError: (err: any, _variables: any, context: any) => {
                if (context?.previousData) {
                    context.previousData.forEach(([key, data]: [any, any]) => {
                        queryClient.setQueryData(key, data);
                    });
                }
                toast.error(err?.message || "Lỗi khi gửi bình luận. Đang hoàn tác.");
            },
            onSettled: () => {
                queryClient.invalidateQueries({ queryKey: trpc.comments.getByEntity.queryKey({ entityType, entityId }) });
                queryClient.invalidateQueries({ queryKey: trpc.activityLog.getByEntity.queryKey({ entityType, entityId }) });
                setTimeout(scrollToTop, 80);
            },
        })
    );

    // Update mutation
    const updateMutation = useMutation(
        trpc.comments.update.mutationOptions({
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: trpc.comments.getByEntity.queryKey({ entityType, entityId }) });
                queryClient.invalidateQueries({ queryKey: trpc.activityLog.getByEntity.queryKey({ entityType, entityId }) });
                setEditingCommentId(null);
                setEditContent("");
            },
            onError: (err: any) => {
                toast.error(err?.message || "Lỗi khi cập nhật");
            }
        })
    );

    // Delete mutation with instant Optimistic deletion
    const deleteMutation = useMutation(
        trpc.comments.delete.mutationOptions({
            onMutate: async ({ id }) => {
                const queryFilter = { queryKey: trpc.comments.getByEntity.queryKey({ entityType, entityId }) };
                await queryClient.cancelQueries(queryFilter);
                const previousData = queryClient.getQueriesData(queryFilter);

                queryClient.setQueriesData(queryFilter, (old: any) => {
                    if (!old || !old.pages) return old;
                    return {
                        ...old,
                        pages: old.pages.map((page: any) => ({
                            ...page,
                            items: (page.items || []).filter((item: any) => item.id !== id),
                        })),
                    };
                });

                return { previousData, queryFilter };
            },
            onError: (err: any, _variables: any, context: any) => {
                if (context?.previousData) {
                    context.previousData.forEach(([key, data]: [any, any]) => {
                        queryClient.setQueryData(key, data);
                    });
                }
                toast.error(err?.message || "Lỗi khi xóa bình luận");
            },
            onSettled: () => {
                queryClient.invalidateQueries({ queryKey: trpc.comments.getByEntity.queryKey({ entityType, entityId }) });
                queryClient.invalidateQueries({ queryKey: trpc.activityLog.getByEntity.queryKey({ entityType, entityId }) });
            },
        })
    );

    const handleSend = useCallback(() => {
        const text = newComment.trim();
        if ((!text && pendingAttachments.length === 0) || createMutation.isPending || isUploading || isComposingRef.current) return;
        
        let finalContent = text;
        if (pendingAttachments.length > 0) {
            const attachmentMarkdown = pendingAttachments.map(att => {
                return att.isImage ? `![${att.fileName}](${att.fileUrl})` : `[${att.fileName}](${att.fileUrl})`;
            }).join('\n');
            finalContent += finalContent ? `\n\n${attachmentMarkdown}` : attachmentMarkdown;
        }
        
        createMutation.mutate({ entityType, entityId, content: finalContent });
        setPendingAttachments([]);
    }, [newComment, pendingAttachments, createMutation, isUploading]);

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Tiền kiểm tra dung lượng file ngay lập tức (0ms)
        if (file.size > MAX_ATTACHMENT_SIZE) {
            toast.error(`Tệp "${file.name}" (${formatBytes(file.size)}) vượt quá giới hạn tối đa cho phép (100MB). Vui lòng nén file hoặc chia sẻ liên kết Google Drive.`);
            if (e.target) e.target.value = '';
            return;
        }

        const tempId = 'temp-' + Date.now();
        const ext = file.name.split('.').pop()?.toLowerCase() || '';
        const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
        const tempBlobUrl = URL.createObjectURL(file);

        // Hiển thị ngay lập tức trong danh sách pending attachments với thumbnail blob
        setPendingAttachments(prev => [...prev, { fileName: file.name, fileUrl: tempBlobUrl, isImage, tempId } as any]);

        try {
            setIsUploading(true);
            const { fileUrl, fileName } = await uploadApi.uploadFile(file);

            try {
                await trpcClient.attachments.create.mutate({
                    entityType,
                    entityId,
                    fileName,
                    fileUrl: getUploadUrl(fileUrl),
                });
                queryClient.invalidateQueries({ queryKey: ['attachments', entityType, entityId] });
                queryClient.invalidateQueries({ queryKey: ['activity-log', entityType, entityId] });
            } catch {}

            // Cập nhật URL chính thức từ máy chủ
            setPendingAttachments(prev => prev.map((att: any) => {
                if (att.tempId === tempId) {
                    return { fileName, fileUrl, isImage };
                }
                return att;
            }));
            
            if (inputRef.current) {
                inputRef.current.style.height = 'auto';
                inputRef.current.style.height = inputRef.current.scrollHeight + 'px';
            }
        } catch (error: any) {
            setPendingAttachments(prev => prev.filter((att: any) => att.tempId !== tempId));
            toast.error(extractErrorMessage(error, 'Lỗi upload file'));
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey && !isComposingRef.current) {
            e.preventDefault();
            handleSend();
        }
    };

    // Auto-resize textarea
    const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setNewComment(e.target.value);
        const el = e.target;
        el.style.height = '60px';
        el.style.height = Math.min(el.scrollHeight, 120) + 'px';
    };

    return (
        <div className="flex flex-col relative gap-3">
            {/* ═══ INPUT AT TOP (Activity Feed: New comments appear right below) ═══ */}
            <div className="border border-slate-200 rounded-lg bg-white overflow-hidden shadow-sm focus-within:border-indigo-400 focus-within:ring-1 focus-within:ring-indigo-400 transition-all">
                <textarea
                    ref={inputRef}
                    value={newComment}
                    onChange={handleInput}
                    onKeyDown={handleKeyDown}
                    onCompositionStart={() => { isComposingRef.current = true; }}
                    onCompositionEnd={() => { isComposingRef.current = false; }}
                    placeholder="Viết ghi chú... (Enter để gửi, Shift+Enter xuống dòng)"
                    rows={2}
                    className={cn(
                        "w-full text-sm px-4 py-3 resize-none overflow-y-auto border-0 bg-transparent",
                        "focus:outline-none focus:ring-0",
                        "placeholder:text-slate-400"
                    )}
                    style={{ height: '60px', maxHeight: '150px' }}
                />
                
                {pendingAttachments.length > 0 && (
                    <div className="px-3 py-2 border-t border-slate-100 bg-slate-50/80 flex flex-wrap gap-2">
                        {pendingAttachments.map((att, idx) => (
                            <div key={idx} className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-md px-2 py-1 text-xs shadow-sm">
                                <Paperclip className="h-3.5 w-3.5 text-slate-400" />
                                <span className="truncate max-w-[150px] font-medium text-slate-700">{att.fileName}</span>
                                <button 
                                    type="button" 
                                    onClick={() => setPendingAttachments(prev => prev.filter((_, i) => i !== idx))}
                                    className="text-slate-400 hover:text-rose-500 transition-colors ml-1 p-0.5 rounded-sm hover:bg-rose-50"
                                >
                                    <X className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                <div className="flex items-center justify-between px-3 py-2 border-t border-slate-100 bg-slate-50">
                    <input
                        type="file"
                        ref={fileInputRef}
                        className="hidden"
                        onChange={handleFileUpload}
                    />
                    <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isUploading}
                        className="flex items-center gap-1.5 text-xs font-medium text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 px-2 py-1.5 rounded-md transition-colors disabled:opacity-50"
                    >
                        {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
                        <span>{isUploading ? 'Đang tải lên...' : 'Đính kèm tệp'}</span>
                    </button>
                    <button
                        onClick={handleSend}
                        disabled={(!newComment.trim() && pendingAttachments.length === 0) || createMutation.isPending || isUploading}
                        className={cn(
                            "flex items-center gap-2 px-4 py-1.5 rounded-md text-sm font-semibold transition-all",
                            (newComment.trim() || pendingAttachments.length > 0)
                                ? "bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm"
                                : "bg-slate-200 text-slate-400 cursor-default"
                        )}
                    >
                        {createMutation.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <Send className="h-4 w-4" />
                        )}
                        Gửi
                    </button>
                </div>
            </div>

            {/* ═══ COMMENT LIST (NEWEST FIRST: Directly below input) ═══ */}
            <div ref={scrollRef} className="space-y-3 max-h-[420px] overflow-y-auto p-1 rounded-lg scroll-smooth bg-slate-50/30">
                {isLoading ? (
                    <div className="flex items-center justify-center py-6">
                        <Loader2 className="h-5 w-5 animate-spin text-slate-300" />
                    </div>
                ) : comments.length === 0 ? (
                    <div className="text-center py-4 text-xs text-slate-400 italic">
                        Chưa có ghi chú nào. Hãy nhập bình luận đầu tiên ở trên!
                    </div>
                ) : (
                    <>
                        {comments.map((comment: any) => {
                            const userName = comment.author?.fullName || comment.userName || "Người dùng";
                            const userAvatar = comment.author?.avatar || comment.userAvatar;
                            const initials = userName.substring(0, 2).toUpperCase();
                            const isMe = comment.authorId === currentUser.user_id || comment.userId === currentUser.user_id;

                            return (
                                <div key={comment.id} className="flex gap-3 group">
                                    <Avatar className="h-7 w-7 border border-slate-200 shrink-0 mt-0.5 shadow-sm">
                                        <AvatarImage src={getUploadUrl(userAvatar)} />
                                        <AvatarFallback className="text-[10px] bg-slate-100 text-slate-600 font-medium">{initials}</AvatarFallback>
                                    </Avatar>
                                    <div className="flex-1 min-w-0 bg-white border border-slate-100 rounded-lg p-3 shadow-sm group-hover:border-slate-200 transition-colors">
                                        <div className="flex items-center gap-2 mb-1.5">
                                            <span className="text-sm font-semibold text-slate-800">{userName}</span>
                                            <span className="text-xs text-slate-400">
                                                {comment.isOptimistic ? (
                                                    <span className="inline-flex items-center gap-1 text-[11px] text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 animate-pulse font-medium">
                                                        <Loader2 className="h-2.5 w-2.5 animate-spin" /> Đang gửi...
                                                    </span>
                                                ) : (
                                                    format(new Date(comment.createdAt), "HH:mm dd/MM/yyyy")
                                                )}
                                            </span>
                                            {/* Edit/delete on hover */}
                                            {isMe && !comment.isOptimistic && (
                                                <div className="hidden group-hover:flex items-center gap-2 text-xs ml-auto">
                                                    <button
                                                        onClick={async () => { setEditingCommentId(comment.id); setEditContent(comment.content); }}
                                                        className="text-slate-400 hover:text-indigo-600 font-medium transition-colors"
                                                    >Sửa</button>
                                                    <span className="text-slate-200">·</span>
                                                    <button
                                                        onClick={async () => { if (await confirm("Xóa ghi chú này?")) deleteMutation.mutate({ id: comment.id }); }}
                                                        className="text-slate-400 hover:text-rose-600 font-medium transition-colors"
                                                    >Xóa</button>
                                                </div>
                                            )}
                                        </div>

                                        {editingCommentId === comment.id ? (
                                            <div className="space-y-2 mt-2">
                                                <textarea
                                                    value={editContent}
                                                    onChange={(e) => setEditContent(e.target.value)}
                                                    className="w-full min-h-[60px] text-sm p-3 border border-indigo-200 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500/20 bg-indigo-50/30"
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' && !e.shiftKey) {
                                                            e.preventDefault();
                                                            updateMutation.mutate({ id: comment.id, content: editContent });
                                                        }
                                                        if (e.key === 'Escape') {
                                                            setEditingCommentId(null);
                                                            setEditContent("");
                                                        }
                                                    }}
                                                    autoFocus
                                                />
                                                <div className="text-xs text-slate-500 flex items-center justify-between">
                                                    <span>Nhấn <strong>Enter</strong> để lưu, <strong>Esc</strong> để hủy</span>
                                                    <div className="flex gap-2">
                                                        <button 
                                                            onClick={() => { setEditingCommentId(null); setEditContent(""); }}
                                                            className="text-slate-500 hover:text-slate-700 px-2 py-1 bg-slate-100 rounded"
                                                        >Hủy</button>
                                                        <button 
                                                            onClick={() => updateMutation.mutate({ id: comment.id, content: editContent })}
                                                            className="text-indigo-600 hover:text-indigo-700 px-2 py-1 bg-indigo-50 font-medium rounded"
                                                        >Lưu</button>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="text-sm text-slate-700 whitespace-pre-wrap break-words leading-relaxed">
                                                {renderContent(comment.content)}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                        {hasNextPage && (
                            <div className="pt-2 pb-1 flex justify-center">
                                <button
                                    onClick={() => fetchNextPage()}
                                    disabled={isFetchingNextPage}
                                    className="text-xs font-medium text-indigo-600 hover:text-indigo-700 bg-indigo-50 hover:bg-indigo-100 px-3 py-1.5 rounded transition-colors disabled:opacity-50 shadow-sm"
                                >
                                    {isFetchingNextPage ? "Đang tải..." : "Tải thêm bình luận cũ hơn"}
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}


